import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import api from '../services/api'
import toast from 'react-hot-toast'
import { 
  Save, 
  Send, 
  ChevronLeft, 
  ChevronRight, 
  Sparkles,
  Upload,
  Plus,
  Trash2
} from 'lucide-react'

const SECTIONS = [
  { key: 'A', name: 'Employee Header', fields: ['employee_code', 'date_of_joining'] },
  { key: 'B', name: 'Personal Information', fields: ['full_name', 'father_husband_name', 'current_designation', 'date_of_birth', 'gender', 'marital_status', 'mother_name', 'spouse_name'] },
  { key: 'C', name: 'Contact & Address', fields: ['permanent_address_line1', 'permanent_address_line2', 'pin_code', 'landmark', 'mobile_number', 'home_phone', 'personal_email'] },
  { key: 'D', name: 'Identity Documents', fields: ['pan_number', 'aadhar_number', 'passport_number', 'passport_valid_upto', 'uan_number', 'pf_option'] },
  { key: 'E', name: 'Bank Details', fields: ['account_holder_name', 'bank_name', 'branch_address', 'account_number', 'ifsc_code', 'account_type'] },
  { key: 'F', name: 'Family Information', fields: ['family_members'] },
  { key: 'G', name: 'Emergency Contact', fields: ['emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_email', 'emergency_contact_relationship'] },
  { key: 'H', name: 'Education', fields: ['education'] },
  { key: 'I', name: 'Experience', fields: ['experience_in_years', 'experience_out_years'] },
  { key: 'J', name: 'Previous Employment', fields: ['previous_employment'] },
  { key: 'K', name: 'HR Fields', fields: ['employee_status', 'employee_type', 'reporting_to', 'source_of_hire', 'department', 'location'] },
]

const EDUCATION_TYPES = [
  '10th',
  '12th',
  'Graduation',
  'Post Graduation',
  'Diploma/Other',
]

const toSectionHash = (section) =>
  `#${section.name.replace(/\s+/g, '-').toLowerCase()}`

function OnboardingForm() {
  const { companySlug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [currentSection, setCurrentSection] = useState(0)
  const [employee, setEmployee] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [aiFields, setAiFields] = useState({})
  const [documents, setDocuments] = useState([])
  const [familyMembers, setFamilyMembers] = useState([])
  const [education, setEducation] = useState([])
  const [employment, setEmployment] = useState([])
  const [comments, setComments] = useState([])
  const [extraDirty, setExtraDirty] = useState(false)
  const [removedFamilyIds, setRemovedFamilyIds] = useState([])
  const [removedEducationIds, setRemovedEducationIds] = useState([])
  const [uploadStatus, setUploadStatus] = useState({ state: 'idle', message: '', documentId: null })

  const { register, handleSubmit, setValue, watch, reset, getValues, formState: { errors, isDirty } } = useForm()

  useEffect(() => {
    fetchEmployeeData()
  }, [companySlug])

  useEffect(() => {
    if (!location.hash) return
    const hash = location.hash.toLowerCase()
    const idx = SECTIONS.findIndex((s) => toSectionHash(s) === hash)
    if (idx >= 0) {
      setCurrentSection(idx)
    }
  }, [location.hash])

  const fetchEmployeeData = async () => {
    try {
      setLoading(true)
      const [empRes, familyRes, eduRes, empHistRes, docRes, commentsRes] = await Promise.all([
        api.get(`/${companySlug}/employee/me`),
        api.get(`/${companySlug}/employee/me/family`),
        api.get(`/${companySlug}/employee/me/education`),
        api.get(`/${companySlug}/employee/me/employment`),
        api.get(`/${companySlug}/documents`),
        api.get(`/${companySlug}/employee/me/comments`)
      ])
      
      const emp = empRes.data
      setEmployee(emp)
      setFamilyMembers(familyRes.data)
      setEducation(eduRes.data)
      setEmployment(empHistRes.data)
      setDocuments(docRes.data)
      setComments(commentsRes.data)

      // Set form values
      Object.keys(emp).forEach(key => {
        if (emp[key] !== null && emp[key] !== undefined) {
          setValue(key, emp[key])
        }
      })
    } catch (error) {
      toast.error('Failed to load employee data')
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data) => {
    if (employee?.is_locked) {
      toast.error('Form is locked. Contact HR to make changes.')
      return false
    }

    setSaving(true)
    try {
      const sectionKey = SECTIONS[currentSection].key
      const updateData = {}
      
      // Map section to update data
      const sectionMap = {
        'A': 'header',
        'B': 'personal',
        'C': 'contact',
        'D': 'identity',
        'E': 'bank',
        'G': 'emergency',
        'I': 'experience'
      }
      
      if (sectionMap[sectionKey]) {
        updateData[sectionMap[sectionKey]] = {}
        SECTIONS[currentSection].fields.forEach(field => {
          if (data[field] !== undefined) {
            updateData[sectionMap[sectionKey]][field] = data[field]
          }
        })
      }

      if (sectionKey === 'F') {
        await saveFamily()
      }

      if (sectionKey === 'H') {
        await saveEducation()
      }

      if (sectionKey === 'J') {
        await saveEmployment()
      }

      // Mark section as complete
      const completionStatus = employee?.section_completion_status || {}
      completionStatus[sectionKey] = true
      updateData.section_completion_status = completionStatus

      await api.put(`/${companySlug}/employee/me`, updateData)

      setEmployee(prev => prev ? { ...prev, section_completion_status: completionStatus } : prev)
      // Reset dirty state to avoid false "unsaved" prompts
      reset(getValues())
      setExtraDirty(false)
      
      toast.success('Section saved successfully')
      return true
    } catch (error) {
      toast.error('Failed to save section')
      return false
    } finally {
      setSaving(false)
    }
  }

  const requestSectionChange = async (nextIndex) => {
    if (nextIndex === currentSection || saving) return

    if (isDirty || extraDirty) {
      const shouldSave = window.confirm(
        'You have unsaved changes. Press OK to save before leaving this section, or Cancel to stay.'
      )
      if (!shouldSave) {
        return
      }
      const saved = await handleSubmit(onSubmit)()
      if (!saved) {
        return
      }
    }

    setCurrentSection(nextIndex)
    const nextSection = SECTIONS[nextIndex]
    if (nextSection) {
      window.history.replaceState(null, '', toSectionHash(nextSection))
    }
  }

  const handleFinalSubmit = async () => {
    if (!window.confirm('Are you sure you want to submit your onboarding form? This action cannot be undone.')) {
      return
    }

    try {
      await api.post(`/${companySlug}/employee/me/submit`)
      toast.success('Onboarding form submitted successfully!')
      navigate(`/${companySlug}/dashboard`)
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit form'
      toast.error(message)
    }
  }

  const handleFileUpload = async (e, documentType) => {
    const file = e.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)

    try {
      setUploadStatus({ state: 'uploading', message: 'Uploading document…', documentId: null })
      toast.loading('Uploading document...', { id: 'upload' })
      const response = await api.post(
        `/${companySlug}/documents/upload?document_type=${documentType}`,
        formData
      )
      toast.success('Document uploaded', { id: 'upload' })
      if (documentType === 'photo') {
        setUploadStatus({ state: 'completed', message: 'Photo uploaded successfully.', documentId: response.data.document_id })
      } else {
        setUploadStatus({ state: 'uploaded', message: 'Upload complete. Preparing extraction…', documentId: response.data.document_id })
      }
      
      // Refresh documents
      const docRes = await api.get(`/${companySlug}/documents`)
      setDocuments(docRes.data)
      
      // Poll for extraction result
      if (documentType !== 'photo') {
        pollExtractionResult(response.data.document_id)
      }
    } catch (error) {
      setUploadStatus({ state: 'error', message: 'Upload failed. Please try again.', documentId: null })
      toast.error('Failed to upload document', { id: 'upload' })
    }
  }

  const pollExtractionResult = async (documentId) => {
    const poll = async () => {
      try {
        const response = await api.get(`/${companySlug}/documents/${documentId}/extraction`)
        const result = response.data
        
        if (result.status === 'completed') {
          // Apply extracted data to form
          if (result.extracted_data) {
            setUploadStatus({ state: 'ai', message: 'AI extracting relevant fields…', documentId })
            applyExtractedData(result.extracted_data)
            setUploadStatus({ state: 'completed', message: 'Extraction complete. Fields filled.', documentId })
            toast.success('AI extracted data from your document!')
          }
        } else if (result.status === 'processing') {
          setUploadStatus({ state: 'processing', message: 'Extracting text and structure…', documentId })
          setTimeout(poll, 3000)
        } else {
          setTimeout(poll, 3000)
        }
      } catch (error) {
        console.error('Error polling extraction:', error)
        setUploadStatus({ state: 'error', message: 'Extraction failed. Please retry.', documentId })
      }
    }
    poll()
  }

  const applyExtractedData = (data) => {
    const fieldMappings = {
      'full_name': 'full_name',
      'aadhar_number': 'aadhar_number',
      'pan_number': 'pan_number',
      'date_of_birth': 'date_of_birth',
      'gender': 'gender',
      'address': 'permanent_address_line1',
      'passport_number': 'passport_number',
      'valid_upto': 'passport_valid_upto',
      'account_holder_name': 'account_holder_name',
      'bank_name': 'bank_name',
      'account_number': 'account_number',
      'ifsc_code': 'ifsc_code',
      'branch_address': 'branch_address',
      'email': 'personal_email',
      'phone': 'mobile_number',
      'current_designation': 'current_designation',
    }

    const newAiFields = {}
    Object.entries(data).forEach(([key, value]) => {
      const formField = fieldMappings[key]
      if (formField && value) {
        setValue(formField, value)
        newAiFields[formField] = true
      }
    })
    setAiFields(prev => ({ ...prev, ...newAiFields }))

    if (data.total_experience !== null && data.total_experience !== undefined) {
      setValue('experience_in_years', data.total_experience)
      setAiFields(prev => ({ ...prev, experience_in_years: true }))
    }

    if (Array.isArray(data.education) && data.education.length > 0) {
      const mapped = data.education.map((e) => ({
        qualification_level: e.level || e.qualification_level || '',
        degree_diploma: e.degree || e.program || e.specialization || '',
        passing_year: e.year || e.passing_year || '',
        institute_name: e.institute || e.institute_name || '',
      }))
      setEducation((prev) => [...prev, ...mapped])
      setExtraDirty(true)
    }

    if (Array.isArray(data.experience) && data.experience.length > 0) {
      const mapped = data.experience.slice(0, 4).map((e) => ({
        company_name: e.company || e.company_name || '',
        designation: e.designation || '',
        from_date: normalizeDate(e.from_date || e.start_date || ''),
        to_date: normalizeDate(e.to_date || e.end_date || ''),
      }))
      setEmployment(mapped)
      setExtraDirty(true)
    }
  }

  const normalizeDate = (value) => {
    if (!value) return ''
    const v = String(value).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v
    const m = v.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/)
    if (m) {
      const day = m[1].padStart(2, '0')
      const month = m[2].padStart(2, '0')
      const year = m[3]
      return `${year}-${month}-${day}`
    }
    const m2 = v.match(/^(\d{4})[\/\-](\d{1,2})$/)
    if (m2) {
      const year = m2[1]
      const month = m2[2].padStart(2, '0')
      return `${year}-${month}-01`
    }
    return ''
  }

  const addEducationRow = () => {
    setEducation((prev) => [...prev, { qualification_level: '', degree_diploma: '', passing_year: '', institute_name: '' }])
    setExtraDirty(true)
  }

  const updateEducationField = (index, field, value) => {
    setEducation((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
    setExtraDirty(true)
  }

  const removeEducationRow = (index) => {
    const entry = education[index]
    if (entry?.id) {
      setRemovedEducationIds((prev) => [...prev, entry.id])
    }
    setEducation((prev) => prev.filter((_, i) => i !== index))
    setExtraDirty(true)
  }

  const updateEmploymentField = (index, field, value) => {
    setEmployment((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
    setExtraDirty(true)
  }

  const saveEducation = async () => {
    for (const id of removedEducationIds) {
      try {
        await api.delete(`/${companySlug}/employee/me/education/${id}`)
      } catch (e) {
        // ignore
      }
    }
    if (removedEducationIds.length) {
      setRemovedEducationIds([])
    }

    const payloads = education
      .filter((e) => e.qualification_level || e.degree_diploma || e.passing_year || e.institute_name)

    for (const entry of payloads) {
      if (!entry.qualification_level) {
        continue
      }
      const payload = {
        qualification_level: entry.qualification_level,
        degree_diploma: entry.degree_diploma || null,
        passing_year: entry.passing_year || null,
        institute_name: entry.institute_name || null,
      }
      if (entry.id) {
        await api.put(`/${companySlug}/employee/me/education/${entry.id}`, payload)
      } else {
        const res = await api.post(`/${companySlug}/employee/me/education`, payload)
        setEducation((prev) => prev.map((e) => (e === entry ? res.data : e)))
      }
    }
  }

  const saveFamily = async () => {
    // Delete removed members
    for (const id of removedFamilyIds) {
      try {
        await api.delete(`/${companySlug}/employee/me/family/${id}`)
      } catch (e) {
        // ignore
      }
    }
    if (removedFamilyIds.length) {
      setRemovedFamilyIds([])
    }

    for (const member of familyMembers) {
      const payload = {
        relationship: member.relationship?.trim() || null,
        name: member.name?.trim() || null,
        date_of_birth: member.date_of_birth || null,
        profession: member.profession?.trim() || null,
      }
      if (member.id) {
        await api.put(`/${companySlug}/employee/me/family/${member.id}`, payload)
      } else {
        const res = await api.post(`/${companySlug}/employee/me/family`, payload)
        setFamilyMembers((prev) => prev.map((m) => (m === member ? res.data : m)))
      }
    }
  }

  const saveEmployment = async () => {
    const payloads = employment
      .map((e) => ({
        ...e,
        from_date: e.from_date || null,
        to_date: e.to_date || null,
      }))
      .filter((e) => e.company_name || e.designation || e.from_date || e.to_date)

    for (const entry of payloads) {
      if (entry.id) {
        await api.put(`/${companySlug}/employee/me/employment/${entry.id}`, {
          from_date: entry.from_date,
          to_date: entry.to_date,
          company_name: entry.company_name || null,
          designation: entry.designation || null,
        })
      } else {
        const res = await api.post(`/${companySlug}/employee/me/employment`, {
          from_date: entry.from_date,
          to_date: entry.to_date,
          company_name: entry.company_name || null,
          designation: entry.designation || null,
        })
        setEmployment((prev) => [...prev.filter((p) => p !== entry), res.data])
      }
    }
  }

  const addFamilyMember = () => {
    setFamilyMembers([...familyMembers, { relationship: '', name: '', date_of_birth: '', profession: '' }])
    setExtraDirty(true)
  }

  const updateFamilyMember = (index, field, value) => {
    const updated = [...familyMembers]
    updated[index][field] = value
    setFamilyMembers(updated)
    setExtraDirty(true)
  }

  const removeFamilyMember = (index) => {
    const member = familyMembers[index]
    if (member?.id) {
      setRemovedFamilyIds((prev) => [...prev, member.id])
    }
    setFamilyMembers(familyMembers.filter((_, i) => i !== index))
    setExtraDirty(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (employee?.is_locked) {
    return (
      <div className="card text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Form Submitted</h2>
        <p className="text-gray-600 mb-6">
          Your onboarding form has been submitted and is currently locked.
          Contact your HR administrator if you need to make changes.
        </p>
        <button
          onClick={() => navigate(`/${companySlug}/dashboard`)}
          className="btn-primary"
        >
          Go to Dashboard
        </button>
      </div>
    )
  }

  const section = SECTIONS[currentSection]
  const commentsBySection = comments.reduce((acc, comment) => {
    const key = comment.section_key || 'Unknown'
    if (!acc[key]) acc[key] = []
    acc[key].push(comment)
    return acc
  }, {})

  const handleRectifyComment = async (commentId) => {
    try {
      const response = await api.patch(`/${companySlug}/employee/me/comments/${commentId}/rectify`, {})
      setComments((prev) => prev.map((c) => (c.id === commentId ? response.data : c)))
      toast.success('Marked as rectified')
    } catch (error) {
      toast.error('Failed to update comment')
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Onboarding Form</h1>
          <span className="text-sm text-gray-500">
            Step {currentSection + 1} of {SECTIONS.length}
          </span>
        </div>
        <div className="flex items-center space-x-2 overflow-x-auto pb-2">
          {SECTIONS.map((s, idx) => (
            <button
              key={s.key}
              onClick={() => requestSectionChange(idx)}
              className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                idx === currentSection
                  ? 'bg-primary-600 text-white'
                  : employee?.section_completion_status?.[s.key]
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="inline-flex items-center">
                {s.key}. {s.name}
                {(commentsBySection[s.key] || []).filter((c) => c.status !== 'rectified').length > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-200 text-amber-900">
                    {(commentsBySection[s.key] || []).filter((c) => c.status !== 'rectified').length}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Document Upload (show on relevant sections) */}
      {['B', 'C', 'D', 'E', 'H', 'J'].includes(section.key) && (
        <div className="card mb-6 bg-amber-50 border-amber-200">
          <div className="flex items-center mb-3">
            <Sparkles className="h-5 w-5 text-amber-500 mr-2" />
            <h3 className="font-medium text-amber-900">AI Document Upload</h3>
          </div>
          <p className="text-sm text-amber-700 mb-3">
            Upload your documents and our AI will automatically fill in the form fields.
          </p>
          <div className="mb-4">
            <div className="text-xs text-amber-800 mb-2">AI extraction progress</div>
            <div className="flex items-center space-x-2 text-xs text-amber-900">
              <span className={`px-2 py-1 rounded ${['uploading','uploaded','processing','ai','completed'].includes(uploadStatus.state) ? 'bg-amber-200' : 'bg-amber-100'}`}>1. Upload</span>
              <span className={`px-2 py-1 rounded ${['processing','ai','completed'].includes(uploadStatus.state) ? 'bg-amber-200' : 'bg-amber-100'}`}>2. Extract</span>
              <span className={`px-2 py-1 rounded ${['ai','completed'].includes(uploadStatus.state) ? 'bg-amber-200' : 'bg-amber-100'}`}>3. AI Parse</span>
              <span className={`px-2 py-1 rounded ${uploadStatus.state === 'completed' ? 'bg-amber-200' : 'bg-amber-100'}`}>4. Fill Form</span>
            </div>
            {uploadStatus.state !== 'idle' && (
              <div className="text-xs text-amber-700 mt-2">{uploadStatus.message}</div>
            )}
          </div>
          <input
            type="file"
            accept=".pdf,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.heic,.webp"
            onChange={(e) => handleFileUpload(e, section.key === 'D' ? 'aadhar' : section.key === 'E' ? 'bank_passbook' : 'resume')}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-amber-100 file:text-amber-700 hover:file:bg-amber-200"
          />
          <p className="text-xs text-amber-600 mt-2">
            Supported: PDF, DOCX, XLSX, JPG, PNG, HEIC, WEBP (max 10MB)
          </p>
        </div>
      )}

      {/* Form Section */}
      <form onSubmit={handleSubmit(onSubmit)} className="card">
        <h2 className="section-header flex items-center">
          {section.key}. {section.name}
          {employee?.section_completion_status?.[section.key] && (
            <span className="ml-3 text-sm font-normal text-green-600 bg-green-100 px-2 py-1 rounded">
              ✓ Completed
            </span>
          )}
        </h2>

        {(commentsBySection[section.key] || []).length > 0 && (
          <div className="mb-6 p-4 rounded-lg border border-amber-200 bg-amber-50">
            <div className="text-sm font-semibold text-amber-900 mb-2">Review Comments</div>
            <div className="space-y-3">
              {(commentsBySection[section.key] || []).map((comment) => (
                <div key={comment.id} className="p-3 rounded-md bg-white border border-amber-100">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm text-gray-800">{comment.comment}</div>
                      {comment.created_by_email && (
                        <div className="text-xs text-gray-500 mt-1">By {comment.created_by_email}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded ${comment.status === 'rectified' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`}>
                        {comment.status === 'rectified' ? 'Rectified' : 'Open'}
                      </span>
                      {comment.status !== 'rectified' && (
                        <button
                          type="button"
                          onClick={() => handleRectifyComment(comment.id)}
                          className="text-xs font-medium text-primary-700 hover:text-primary-900"
                        >
                          Mark Rectified
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Section A - Employee Header */}
          {section.key === 'A' && (
            <>
              <div className="mb-6">
                <label className="label">Passport Photo (3:4)</label>
                <p className="text-xs text-gray-500 mb-2">Upload a clear, front-facing photo (JPG/PNG/HEIC).</p>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.heic,.webp"
                  onChange={(e) => handleFileUpload(e, 'photo')}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Employee Code (optional)</label>
                  <input
                    {...register('employee_code')}
                    className={`input ${aiFields.employee_code ? 'ai-suggestion ai-highlight' : ''}`}
                    placeholder="Auto-generated"
                    readOnly
                  />
                  {aiFields.employee_code && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center">
                      <Sparkles className="h-3 w-3 mr-1" /> AI suggested
                    </p>
                  )}
                </div>
                <div>
                  <label className="label">Date of Joining *</label>
                  <input
                    type="date"
                    {...register('date_of_joining', { required: true })}
                    className="input"
                  />
                </div>
              </div>
            </>
          )}

          {/* Section B - Personal Information */}
          {section.key === 'B' && (
            <>
              <div>
                <label className="label">Full Name (Block Letters) *</label>
                <input
                  {...register('full_name', { required: true })}
                  className={`input uppercase ${aiFields.full_name ? 'ai-suggestion ai-highlight' : ''}`}
                  placeholder="ENTER YOUR FULL NAME"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Father's / Husband's Name *</label>
                  <input
                    {...register('father_husband_name', { required: true })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Current Designation *</label>
                  <input
                    {...register('current_designation', { required: true })}
                    className={`input ${aiFields.current_designation ? 'ai-suggestion ai-highlight' : ''}`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="label">Date of Birth *</label>
                  <input
                    type="date"
                    {...register('date_of_birth', { required: true })}
                    className={`input ${aiFields.date_of_birth ? 'ai-suggestion ai-highlight' : ''}`}
                  />
                </div>
                <div>
                  <label className="label">Gender *</label>
                  <select {...register('gender', { required: true })} className="input">
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="label">Marital Status *</label>
                  <select {...register('marital_status', { required: true })} className="input">
                    <option value="">Select</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Mother's Name</label>
                  <input {...register('mother_name')} className="input" />
                </div>
                <div>
                  <label className="label">Spouse's Name</label>
                  <input {...register('spouse_name')} className="input" />
                </div>
              </div>
            </>
          )}

          {/* Section C - Contact & Address */}
          {section.key === 'C' && (
            <>
              <div>
                <label className="label">Permanent Address Line 1 *</label>
                <input
                  {...register('permanent_address_line1', { required: true })}
                  className={`input ${aiFields.permanent_address_line1 ? 'ai-suggestion ai-highlight' : ''}`}
                />
              </div>
              <div>
                <label className="label">Permanent Address Line 2</label>
                <input {...register('permanent_address_line2')} className="input" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="label">Pin Code *</label>
                  <input
                    {...register('pin_code', { required: true, pattern: /^\d{6}$/ })}
                    className="input"
                    maxLength={6}
                  />
                </div>
                <div>
                  <label className="label">Landmark</label>
                  <input {...register('landmark')} className="input" />
                </div>
                <div>
                  <label className="label">Mobile Number *</label>
                  <input
                    {...register('mobile_number', { required: true, pattern: /^\d{10}$/ })}
                    className="input"
                    maxLength={10}
                    placeholder="10 digits"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Home Phone</label>
                  <input {...register('home_phone')} className="input" />
                </div>
                <div>
                  <label className="label">Personal Email</label>
                  <input type="email" {...register('personal_email')} className="input" />
                </div>
              </div>
            </>
          )}

          {/* Section D - Identity Documents */}
          {section.key === 'D' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">PAN Number *</label>
                  <input
                    {...register('pan_number', { required: true, pattern: /^[A-Z]{5}\d{4}[A-Z]$/ })}
                    className={`input uppercase ${aiFields.pan_number ? 'ai-suggestion ai-highlight' : ''}`}
                    placeholder="AAAAA9999A"
                    maxLength={10}
                  />
                </div>
                <div>
                  <label className="label">Aadhar Number *</label>
                  <input
                    {...register('aadhar_number', { required: true, pattern: /^\d{12}$/ })}
                    className={`input ${aiFields.aadhar_number ? 'ai-suggestion ai-highlight' : ''}`}
                    placeholder="12 digits"
                    maxLength={12}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Passport Number</label>
                  <input
                    {...register('passport_number')}
                    className={`input uppercase ${aiFields.passport_number ? 'ai-suggestion ai-highlight' : ''}`}
                  />
                </div>
                <div>
                  <label className="label">Passport Valid Up To</label>
                  <input type="date" {...register('passport_valid_upto')} className="input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">UAN Number</label>
                  <input {...register('uan_number')} className="input" placeholder="Universal Account Number" />
                </div>
                <div>
                  <label className="label">PF Option</label>
                  <select {...register('pf_option')} className="input">
                    <option value="">Select</option>
                    <option value="Opt-in">Opt-in</option>
                    <option value="Opt-out">Opt-out</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Section E - Bank Details */}
          {section.key === 'E' && (
            <>
              <div>
                <label className="label">Name of Account Holder *</label>
                <input
                  {...register('account_holder_name', { required: true })}
                  className={`input ${aiFields.account_holder_name ? 'ai-suggestion ai-highlight' : ''}`}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Bank Name *</label>
                  <input
                    {...register('bank_name', { required: true })}
                    className={`input ${aiFields.bank_name ? 'ai-suggestion ai-highlight' : ''}`}
                  />
                </div>
                <div>
                  <label className="label">IFSC Code *</label>
                  <input
                    {...register('ifsc_code', { required: true })}
                    className={`input uppercase ${aiFields.ifsc_code ? 'ai-suggestion ai-highlight' : ''}`}
                    placeholder="AAAA0XXXXXX"
                  />
                </div>
              </div>
              <div>
                <label className="label">Branch Address</label>
                <input
                  {...register('branch_address')}
                  className={`input ${aiFields.branch_address ? 'ai-suggestion ai-highlight' : ''}`}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Account Number *</label>
                  <input
                    {...register('account_number', { required: true })}
                    className={`input ${aiFields.account_number ? 'ai-suggestion ai-highlight' : ''}`}
                  />
                </div>
                <div>
                  <label className="label">Account Type *</label>
                  <select {...register('account_type', { required: true })} className="input">
                    <option value="">Select</option>
                    <option value="Current">Current</option>
                    <option value="Saving">Saving</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Section F - Family Information */}
          {section.key === 'F' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">Add at least 3 family members</p>
                <button
                  type="button"
                  onClick={addFamilyMember}
                  className="flex items-center text-primary-600 hover:text-primary-700"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Member
                </button>
              </div>
              {familyMembers.map((member, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg mb-4">
                  <input
                    placeholder="Relationship"
                    value={member.relationship}
                    onChange={(e) => updateFamilyMember(idx, 'relationship', e.target.value)}
                    className="input"
                  />
                  <input
                    placeholder="Name"
                    value={member.name}
                    onChange={(e) => updateFamilyMember(idx, 'name', e.target.value)}
                    className="input"
                  />
                  <input
                    placeholder="Date of Birth"
                    type="date"
                    value={member.date_of_birth || ''}
                    onChange={(e) => updateFamilyMember(idx, 'date_of_birth', e.target.value)}
                    className="input"
                  />
                  <div className="flex items-center space-x-2">
                    <input
                      placeholder="Profession"
                      value={member.profession}
                      onChange={(e) => updateFamilyMember(idx, 'profession', e.target.value)}
                      className="input flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeFamilyMember(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Section G - Emergency Contact */}
          {section.key === 'G' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Emergency Contact Name *</label>
                  <input
                    {...register('emergency_contact_name', { required: true })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Emergency Contact Phone *</label>
                  <input
                    {...register('emergency_contact_phone', { required: true })}
                    className="input"
                    placeholder="10 digits"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Emergency Contact Email</label>
                  <input type="email" {...register('emergency_contact_email')} className="input" />
                </div>
                <div>
                  <label className="label">Relationship</label>
                  <input {...register('emergency_contact_relationship')} className="input" />
                </div>
              </div>
            </>
          )}

          {/* Section H - Education */}
          {section.key === 'H' && (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-600">Add all your qualifications. You can add multiple entries.</p>
                <button
                  type="button"
                  onClick={addEducationRow}
                  className="flex items-center text-primary-600 hover:text-primary-700"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Education
                </button>
              </div>
              {education.map((entry, idx) => (
                <div key={entry.id || idx} className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg mb-4">
                  <select
                    className="input"
                    value={entry.qualification_level || ''}
                    onChange={(e) => updateEducationField(idx, 'qualification_level', e.target.value)}
                  >
                    <option value="">Select Type</option>
                    {EDUCATION_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <input
                    placeholder="Degree/Diploma (e.g., BBA, MBA)"
                    className="input"
                    value={entry.degree_diploma || ''}
                    onChange={(e) => updateEducationField(idx, 'degree_diploma', e.target.value)}
                  />
                  <input
                    placeholder="Passing Year"
                    className="input"
                    value={entry.passing_year || ''}
                    onChange={(e) => updateEducationField(idx, 'passing_year', e.target.value)}
                  />
                  <input
                    placeholder="College / University"
                    className="input"
                    value={entry.institute_name || ''}
                    onChange={(e) => updateEducationField(idx, 'institute_name', e.target.value)}
                  />
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => removeEducationRow(idx)}
                      className="text-red-500 hover:text-red-700"
                      title="Remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Section I - Experience */}
          {section.key === 'I' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Experience In (years)</label>
                  <input
                    type="number"
                    step="0.1"
                    {...register('experience_in_years')}
                    className="input"
                    placeholder="Current domain"
                  />
                </div>
                <div>
                  <label className="label">Experience Out of (years)</label>
                  <input
                    type="number"
                    step="0.1"
                    {...register('experience_out_years')}
                    className="input"
                    placeholder="Outside domain"
                  />
                </div>
              </div>
            </>
          )}

          {/* Section J - Previous Employment */}
          {section.key === 'J' && (
            <>
              <p className="text-sm text-gray-600 mb-4">Add your previous employment history (up to 4)</p>
              {employment.map((emp, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg mb-4">
                  <input
                    type="date"
                    placeholder="From Date"
                    className="input"
                    value={emp.from_date || ''}
                    onChange={(e) => updateEmploymentField(idx, 'from_date', e.target.value)}
                  />
                  <input
                    type="date"
                    placeholder="To Date"
                    className="input"
                    value={emp.to_date || ''}
                    onChange={(e) => updateEmploymentField(idx, 'to_date', e.target.value)}
                  />
                  <input
                    placeholder="Company Name"
                    className="input"
                    value={emp.company_name || ''}
                    onChange={(e) => updateEmploymentField(idx, 'company_name', e.target.value)}
                  />
                  <input
                    placeholder="Designation"
                    className="input"
                    value={emp.designation || ''}
                    onChange={(e) => updateEmploymentField(idx, 'designation', e.target.value)}
                  />
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setEmployment([...employment, {}])
                  setExtraDirty(true)
                }}
                className="flex items-center text-primary-600 hover:text-primary-700"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Employment
              </button>
            </>
          )}

          {/* Section K - HR Fields */}
          {section.key === 'K' && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800">
                  These fields are typically filled by HR. Contact your HR administrator if any information needs to be updated.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Employee Status</label>
                  <select {...register('employee_status')} className="input" disabled>
                    <option value="Probation">Probation</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Resigned">Resigned</option>
                  </select>
                </div>
                <div>
                  <label className="label">Employee Type</label>
                  <select {...register('employee_type')} className="input" disabled>
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Intern">Intern</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Reporting To</label>
                  <input {...register('reporting_to')} className="input" disabled />
                </div>
                <div>
                  <label className="label">Source of Hire</label>
                  <select {...register('source_of_hire')} className="input" disabled>
                    <option value="">Select</option>
                    <option value="Referral">Referral</option>
                    <option value="Job Portal">Job Portal</option>
                    <option value="Direct">Direct</option>
                    <option value="Campus">Campus</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Department</label>
                  <input {...register('department')} className="input" disabled />
                </div>
                <div>
                  <label className="label">Location</label>
                  <input {...register('location')} className="input" disabled />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => requestSectionChange(Math.max(0, currentSection - 1))}
            disabled={currentSection === 0}
            className="btn-secondary flex items-center disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4 mr-2" /> Previous
          </button>

          <div className="flex items-center space-x-4">
            <button
              type="submit"
              disabled={saving}
              className="btn-secondary flex items-center"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Section
            </button>

            {currentSection === SECTIONS.length - 1 ? (
              <button
                type="button"
                onClick={handleFinalSubmit}
                className="btn-primary flex items-center bg-green-600 hover:bg-green-700"
              >
                <Send className="h-4 w-4 mr-2" />
                Submit Form
              </button>
            ) : (
              <button
                type="button"
                onClick={() => requestSectionChange(currentSection + 1)}
                className="btn-primary flex items-center"
              >
                Next <ChevronRight className="h-4 w-4 ml-2" />
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  )
}

export default OnboardingForm
